import { useEffect, useState } from "react";
import { Loader2, Mail, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/KpiCard";
import { LogementRow, NouveauLogement } from "@/components/LogementCard";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg } from "@/lib/api";
import { ImmeublesPanel } from "@/components/ImmeublesPanel";

export const RappelsPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/rappels").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/rappels", { actif: cfg.actif, jours_avant_echeance: Number(cfg.jours_avant_echeance), jours_avant_relance: Number(cfg.jours_avant_relance), import_mensuel: cfg.import_mensuel });
      setCfg(data); toast.success("Rappels enregistrés");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const { data } = await api.post("/rappels/test"); toast.success(`Courriel envoyé à ${data.email}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  if (!cfg) return null;
  return (
    <Panel title="Rappels par courriel" testId="rappels-panel">
      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <Switch checked={cfg.actif} onCheckedChange={(v) => setCfg({ ...cfg, actif: v })} data-testid="rappels-actif-switch" />
          <span className="text-sm">Recevoir les rappels à <b data-testid="rappels-email">{cfg.email}</b> (chaque matin à 8 h)</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Jours avant l'échéance TPS/TVQ</Label>
            <Input type="number" min="0" max="60" value={cfg.jours_avant_echeance} onChange={(e) => setCfg({ ...cfg, jours_avant_echeance: e.target.value })} data-testid="rappels-jours-echeance" /></div>
          <div className="space-y-1"><Label className="text-xs">Jours avant une date de relance</Label>
            <Input type="number" min="0" max="30" value={cfg.jours_avant_relance} onChange={(e) => setCfg({ ...cfg, jours_avant_relance: e.target.value })} data-testid="rappels-jours-relance" /></div>
        </div>
        <label className="flex items-center gap-3">
          <Switch checked={cfg.import_mensuel} disabled={!cfg.actif} onCheckedChange={(v) => setCfg({ ...cfg, import_mensuel: v })} data-testid="rappels-import-mensuel-switch" />
          <span className="text-sm">Le 1er de chaque mois : rappel d'importer les fichiers du mois précédent (Desjardins, Airbnb, Booking)</span>
        </label>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="rappels-save-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Enregistrer
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="rappels-test-button">
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Envoyer un essai
          </Button>
        </div>
      </div>
    </Panel>
  );
};

export const LogementsPanel = () => {
  const { options, refreshOptions } = useFilters();
  const [cfg, setCfg] = useState([]);
  const [immeubles, setImmeubles] = useState([]);
  const [ajout, setAjout] = useState(false);
  const charger = () => Promise.all([api.get("/logements/config"), api.get("/immeubles")])
    .then(([c, i]) => { setCfg(c.data); setImmeubles(i.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { charger(); }, []);
  const upsert = async (body, msg) => {
    try {
      const { data } = await api.put("/logements/config", body);
      toast.success(msg || (data.deplacees ? `Logement enregistré : ${data.deplacees} réservation(s) rattachée(s) à « ${data.nom} »` : "Logement enregistré"));
      await charger(); refreshOptions(); setAjout(false);
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const supprimer = async (nom) => {
    if (!window.confirm(`Supprimer la fiche du logement « ${nom} » ? Ses réservations et opérations ne sont pas supprimées.`)) { return; }
    try { await api.delete("/logements/config", { params: { nom } }); toast.success("Fiche du logement supprimée"); await charger(); refreshOptions(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const parNom = Object.fromEntries(cfg.map((g) => [g.nom, g]));
  const noms = [...new Set([...cfg.map((g) => g.nom), ...options.logements])];
  return (
    <div className="space-y-6">
    <ImmeublesPanel immeubles={immeubles} onSaved={() => { charger(); refreshOptions(); }} />
    <Panel title="Logements" testId="logements-panel"
      action={<Button size="sm" variant="outline" onClick={() => setAjout(true)} data-testid="add-logement-button"><Plus className="h-3.5 w-3.5 mr-1" />Logement</Button>}>
      <p className="text-xs text-slate-500 mb-4">Pour chaque logement : nom, immeuble (choisi dans la liste des immeubles), taxabilité, adresse, niveaux, type et vocation. La TPS/TVQ non créditable (logement non taxable, ou taxe que le logement ne perçoit pas) est indiquée dans le commentaire des opérations. Les modifications sont enregistrées dès que vous quittez un champ. Pour fusionner deux logements, renommez l'un avec le nom exact de l'autre.</p>
      <div className="space-y-3">
        {ajout && <NouveauLogement immeubles={immeubles} onAdd={(f) => upsert(f, `Logement « ${f.nom} » ajouté`)} onCancel={() => setAjout(false)} />}
        {noms.length === 0 && !ajout && <p className="text-sm text-slate-500">Aucun logement pour l'instant.</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {noms.map((l) => <LogementRow key={l} nom={l} cfg={parNom[l]} existants={noms} immeubles={immeubles} onRenamed={() => { charger(); refreshOptions(); }} onCfg={(b) => upsert(b)} onDelete={supprimer} />)}
      </div>
      </div>
    </Panel>
    </div>
  );
};
